/**
 * Add/Edit modals for the four Form 4180 interview line-item lists (03-17), built
 * on the shared `CrudDialog`. Each maps its camelCase form values to the
 * snake_case payload the nested child endpoints expect; empty dates are sent as
 * `null` (the backend `DateField`s are nullable) rather than "".
 */

import * as Yup from "yup";
import { FormField } from "@/components/common/form-field";
import { CrudDialog } from "@/features/worksheets/components/crud-dialog";
import {
  banksApi,
  eftpsApi,
  individualsApi,
  positionsApi,
  type BankItem,
  type EftpsItem,
  type IndividualItem,
  type PositionItem,
} from "../api";

const dateOrNull = (v: string) => (v ? v : null);

interface DialogBase {
  submissionId: string;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onSaved: () => void;
}

// --- Individuals (Section 2.h) ----------------------------------------------

interface IndividualValues { name: string; phone: string; dateFrom: string; dateTo: string }

export function IndividualDialog({
  submissionId, record, open, onOpenChange, onSaved,
}: DialogBase & { record: IndividualItem | null }) {
  const initialValues: IndividualValues = {
    name: record?.name ?? "",
    phone: record?.phone ?? "",
    dateFrom: record?.dateFrom ?? "",
    dateTo: record?.dateTo ?? "",
  };
  async function onSave(v: IndividualValues) {
    const payload = {
      name: v.name,
      phone: v.phone,
      date_from: dateOrNull(v.dateFrom),
      date_to: dateOrNull(v.dateTo),
    };
    if (record) await individualsApi.update(submissionId, record.id, payload);
    else await individualsApi.create(submissionId, payload);
  }
  return (
    <CrudDialog
      open={open}
      onOpenChange={onOpenChange}
      editing={record !== null}
      noun="Individual"
      initialValues={initialValues}
      validationSchema={Yup.object({ name: Yup.string().required("Name is required.") })}
      onSave={onSave}
      onSaved={onSaved}
    >
      <FormField name="name" label="Name" />
      <FormField name="phone" label="Contact number" />
      <div className="grid grid-cols-2 gap-4">
        <FormField name="dateFrom" label="Dates from" type="date" />
        <FormField name="dateTo" label="Dates to" type="date" />
      </div>
    </CrudDialog>
  );
}

// --- Banks (Section 2.i) ----------------------------------------------------

interface BankValues { bankName: string; accountNumber: string; dateFrom: string; dateTo: string }

export function BankDialog({
  submissionId, record, open, onOpenChange, onSaved,
}: DialogBase & { record: BankItem | null }) {
  const initialValues: BankValues = {
    bankName: record?.bankName ?? "",
    // A masked value (from the list) starts with "•"; don't prefill it back as
    // the real number — leave blank so the practitioner re-enters or edits via
    // the full value fetched on open. (Edit dialogs are opened with the full
    // record via a single retrieve — see the list component.)
    accountNumber: record?.accountNumber?.startsWith("•") ? "" : record?.accountNumber ?? "",
    dateFrom: record?.dateFrom ?? "",
    dateTo: record?.dateTo ?? "",
  };
  async function onSave(v: BankValues) {
    const payload = {
      bank_name: v.bankName,
      account_number: v.accountNumber,
      date_from: dateOrNull(v.dateFrom),
      date_to: dateOrNull(v.dateTo),
    };
    if (record) await banksApi.update(submissionId, record.id, payload);
    else await banksApi.create(submissionId, payload);
  }
  return (
    <CrudDialog
      open={open}
      onOpenChange={onOpenChange}
      editing={record !== null}
      noun="Bank"
      initialValues={initialValues}
      validationSchema={Yup.object({ bankName: Yup.string().required("Bank name is required.") })}
      onSave={onSave}
      onSaved={onSaved}
    >
      <FormField name="bankName" label="Bank name" />
      <FormField name="accountNumber" label="Account number" autoComplete="off" />
      <div className="grid grid-cols-2 gap-4">
        <FormField name="dateFrom" label="Dates from" type="date" />
        <FormField name="dateTo" label="Dates to" type="date" />
      </div>
    </CrudDialog>
  );
}

// --- EFTPS locations (Section 4.3) ------------------------------------------

interface EftpsValues { whereEftps: string; whomEftps: string }

export function EftpsDialog({
  submissionId, record, open, onOpenChange, onSaved,
}: DialogBase & { record: EftpsItem | null }) {
  const initialValues: EftpsValues = {
    whereEftps: record?.whereEftps ?? "",
    whomEftps: record?.whomEftps ?? "",
  };
  async function onSave(v: EftpsValues) {
    const payload = { where_eftps: v.whereEftps, whom_eftps: v.whomEftps };
    if (record) await eftpsApi.update(submissionId, record.id, payload);
    else await eftpsApi.create(submissionId, payload);
  }
  return (
    <CrudDialog
      open={open}
      onOpenChange={onOpenChange}
      editing={record !== null}
      noun="EFTPS Location"
      initialValues={initialValues}
      validationSchema={Yup.object({ whereEftps: Yup.string().required("Location is required.") })}
      onSave={onSave}
      onSaved={onSaved}
    >
      <FormField name="whereEftps" label="Where" />
      <FormField name="whomEftps" label="To whom are the PINs/passwords assigned" />
    </CrudDialog>
  );
}

// --- Corporate positions (Section 4.1) --------------------------------------

interface PositionValues { position: string; name: string; address: string; dateFrom: string; dateTo: string }

export function PositionDialog({
  submissionId, record, open, onOpenChange, onSaved,
}: DialogBase & { record: PositionItem | null }) {
  const initialValues: PositionValues = {
    position: record?.position ?? "",
    name: record?.name ?? "",
    address: record?.address ?? "",
    dateFrom: record?.dateFrom ?? "",
    dateTo: record?.dateTo ?? "",
  };
  async function onSave(v: PositionValues) {
    const payload = {
      position: v.position,
      name: v.name,
      address: v.address,
      date_from: dateOrNull(v.dateFrom),
      date_to: dateOrNull(v.dateTo),
    };
    if (record) await positionsApi.update(submissionId, record.id, payload);
    else await positionsApi.create(submissionId, payload);
  }
  return (
    <CrudDialog
      open={open}
      onOpenChange={onOpenChange}
      editing={record !== null}
      noun="Position"
      initialValues={initialValues}
      validationSchema={Yup.object({ position: Yup.string().required("Position is required.") })}
      onSave={onSave}
      onSaved={onSaved}
    >
      <FormField name="position" label="Position (e.g. president, director)" />
      <FormField name="name" label="Name" />
      <FormField name="address" label="Address" />
      <div className="grid grid-cols-2 gap-4">
        <FormField name="dateFrom" label="From" type="date" />
        <FormField name="dateTo" label="To" type="date" />
      </div>
    </CrudDialog>
  );
}
